import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useEffect, useState, useCallback } from 'react';
import { isNull } from 'lodash';
import { useTheme, Box, Button, ButtonContainer, Checkbox, Chip, DataGrid, FormControlLabel, Grid, Icon, MenuItem, Modal, ModalFooter, Spacer, Stack, TextField, Tooltip, Typography, useSnackbar, ScrollableDrawer, SearchField } from '@emburse/embark-core';
import { AuthenticationIcon, CheckIcon, ErrorIcon, PendingIcon, SyncIcon } from '@emburse/embark-icons';
import { Title, IconMenu } from '../../../components';
import { saveDataInterchange, runDataInterchange } from '../../../api/dataInterchange';
import { DataInterchangeRunState } from '../../../interfaces/DataInterchange';
import { userTimezoneString } from '../../../util/environment';
import { friendlyRegex } from '../../../util/helpers';
import { getTimezone, isValidDate, formatDate, formatDateTime, dateToISOString, dayJsFormatDate, calculateSeconds } from '../../../util/dateAdapter';
import { InterchangeTableContext } from './InterchangeTableProvider';
import DayChip from './DayChip';
import { $t, useTranslation } from '../../../hooks/useTranslation';
const convertRegexToFriendly = (regex) => friendlyRegex(regex).join(' ');
function InterchangeTable({ loading, data, updateData }) {
    useTranslation();
    const theme = useTheme();
    const { selectedRow, setSelectedRow } = useContext(InterchangeTableContext);
    const [showDrawer, setShowDrawer] = useState(false);
    const [runModalOpen, setRunModalOpen] = useState(false);
    const [dataInterchangeNotes, setDataInterchangeNotes] = useState('');
    const [filterLoading, setFilterLoading] = useState(false);
    const [filteredData, setFilteredData] = useState([]);
    const [isFilteringData, setIsFilteringData] = useState(false);
    const [isSavingInterchange, setIsSavingInterchange] = useState(false);
    const [searchFilter, setSearchFilter] = useState('');
    const { enqueueSnackbar } = useSnackbar();
    useEffect(() => {
        if (searchFilter.length === 0) {
            setFilteredData([]);
            setIsFilteringData(false);
        }
        else {
            setFilteredData(data.filter((value) => value.Name.toLowerCase().includes(searchFilter.toLowerCase())));
            setIsFilteringData(true);
        }
        setFilterLoading(false);
    }, [data, searchFilter]);
    const searchChangeHandler = useCallback((value) => {
        if (!filterLoading) {
            setFilterLoading(true);
        }
        setSearchFilter(value);
    }, [filterLoading]);
    const getScheduledStartTime = () => {
        const dat = new Date(selectedRow.Sched_StartTime);
        if (isValidDate(dat)) {
            const isoDate = dateToISOString(dat);
            const today = dayJsFormatDate(new Date(), 'YYYY-MM-DD');
            const concatDate = `${today}${isoDate.slice(10)}`;
            const finalDate = new Date(concatDate);
            return formatDate(finalDate, 'fullTime24h');
        }
        return '';
    };
    const schedStartTimeChangeHandler = (e) => {
        const [hours, minutes, seconds] = e.target.value.split(':');
        const date = new Date();
        date.setHours(parseInt(hours, 10) || 0);
        date.setMinutes(parseInt(minutes, 10) || 0);
        date.setSeconds(parseInt(seconds, 10) || 0);
        setSelectedRow({
            ...selectedRow,
            Sched_StartTime: date
        });
    };
    const runModalFooter = () => (_jsx(ModalFooter, { children: _jsxs(ButtonContainer, { alignButtons: "right", children: [_jsx(Button, { variant: "outlined", onClick: () => setRunModalOpen(false), children: $t('Cancel') }), _jsx(Button, { onClick: () => {
                        runDataInterchange(selectedRow.PK);
                        setRunModalOpen(false);
                    }, children: $t('Confirm') })] }) }));
    return (_jsxs(_Fragment, { children: [_jsx(Spacer, { size: 30 }), _jsx(SearchField, { label: $t('Search'), id: "activity-search-input", disabled: loading, doSearchOnChange: true, onChangeTimeout: 250, searchAction: searchChangeHandler }), _jsx(Spacer, { size: 20 }), _jsx(Button, { endIcon: _jsx(Icon, { iconUrl: SyncIcon }), size: "small", variant: "outlined", onClick: () => {
                    if (!loading) {
                        updateData();
                    }
                }, children: "Refresh" }), _jsx(Spacer, { size: 30 }), _jsx(DataGrid, { loading: loading || filterLoading, autoHeight: true, pageSizeOptions: [5, 10, 20], localeText: {
                    noRowsLabel: $t('You have no interchanges yet. Create an interchange to get started.')
                }, columnVisibilityModel: { id: false }, columns: [
                    {
                        field: 'id',
                        headerName: ''
                    },
                    {
                        field: 'Inactive',
                        headerName: $t('Status'),
                        width: 120,
                        renderCell: (cell) => {
                            const labelVal = () => {
                                if (cell.value) {
                                    return $t('Inactive');
                                }
                                if (!cell.row.isActiveInUI) {
                                    return $t('Unscheduled');
                                }
                                return $t('Active');
                            };
                            return (_jsx(Chip, { size: "mini", variant: "outlined", label: labelVal(), color: cell.value || !cell.row.isActiveInUI
                                    ? 'monochrome'
                                    : 'success' }));
                        }
                    },
                    {
                        field: 'Name',
                        headerName: $t('Name'),
                        width: 290,
                        renderCell: (cell) => {
                            const disabled = !cell.row.isActiveInUI;
                            return (_jsx(Tooltip, { title: cell.value, children: _jsx(Typography, { style: {
                                        color: disabled
                                            ? theme.palette.text.disabled
                                            : 'initial'
                                    }, noWrap: true, children: cell.value }) }));
                        }
                    },
                    {
                        field: 'LastStart',
                        headerName: $t('Last Run'),
                        width: 484,
                        sortComparator: (v1, v2) => {
                            const lastDate = !v1 ? new Date() : new Date(v1);
                            const nextDate = !v2 ? new Date() : new Date(v2);
                            return lastDate.getTime() - nextDate.getTime();
                        },
                        renderCell: (cell) => {
                            const disabled = !cell.row.isActiveInUI;
                            const [year, month, day, hours, minutes, seconds, milliseconds] = cell.value
                                .slice(0, -1)
                                .split(/[- :T.]/)
                                .map(Number);
                            const startTimestamp = Date.UTC(year, month - 1, day, hours, minutes, seconds, milliseconds);
                            const startTime = new Date(startTimestamp);
                            const icon = (() => {
                                if (!isValidDate(startTime)) {
                                    return ErrorIcon;
                                }
                                if (cell.row.RunState === DataInterchangeRunState.Running) {
                                    return SyncIcon;
                                }
                                if (cell.row.RunState === DataInterchangeRunState.Queued) {
                                    return PendingIcon;
                                }
                                if (cell.row.LastRunError) {
                                    return ErrorIcon;
                                }
                                return CheckIcon;
                            })();
                            const color = (() => {
                                if (disabled) {
                                    return theme.palette.text.disabled;
                                }
                                if (cell.row.RunState === DataInterchangeRunState.Queued) {
                                    return theme.palette.warning.main;
                                }
                                if (cell.row.LastRunError) {
                                    return theme.palette.error.main;
                                }
                                return theme.palette.success.main;
                            })();
                            const tooltip = (() => {
                                if (!isValidDate(startTime)) {
                                    return $t('No run history');
                                }
                                if (cell.row.RunState === DataInterchangeRunState.Running) {
                                    return $t('Currently running');
                                }
                                if (cell.row.RunState === DataInterchangeRunState.Queued) {
                                    return $t('Queued for run');
                                }
                                if (cell.row.LastRunError) {
                                    return $t('An internal error has occurred. Please contact our Support team for more information.');
                                }
                                return $t('Successfully ran');
                            })();
                            const renderedDate = () => {
                                if (!isValidDate(startTime)) {
                                    return $t('No run history');
                                }
                                const duration = parseInt(cell.row.LastRunDuration, 10);
                                return `${formatDateTime(startTime)} ${getTimezone(startTime)} - ${calculateSeconds(duration / 1000)}`;
                            };
                            const dateTooltip = () => {
                                if (!isValidDate(startTime)) {
                                    return $t('No run history');
                                }
                                const duration = parseInt(cell.row.LastRunDuration, 10);
                                const endTime = new Date(startTimestamp + duration);
                                return `Run Ended: ${formatDateTime(endTime)} ${getTimezone(startTime)}`;
                            };
                            return (_jsxs(Grid, { container: true, direction: "row", children: [_jsx(Tooltip, { title: tooltip, placement: "left", children: _jsx(Icon, { style: { color }, iconUrl: icon }) }), _jsx(Tooltip, { title: dateTooltip(), placement: "right", children: _jsx(Typography, { style: {
                                                marginLeft: 14,
                                                color: disabled
                                                    ? theme.palette.text.disabled
                                                    : 'initial'
                                            }, display: "inline", children: renderedDate() }) })] }));
                        }
                    },
                    {
                        field: 'FilePatterns',
                        headerName: $t('File Pattern'),
                        width: 310,
                        renderCell: (cell) => {
                            const disabled = !cell.row.isActiveInUI;
                            const filePatterns = cell.value;
                            if (filePatterns.length) {
                                const convertedPatterns = filePatterns.map(convertRegexToFriendly);
                                return (_jsx(_Fragment, { children: convertedPatterns.map((regex, id) => (_jsx(Tooltip, { title: filePatterns[id], children: _jsx(Typography, { style: {
                                                color: disabled
                                                    ? theme.palette.text.disabled
                                                    : 'initial'
                                            }, children: regex }) }, regex))) }));
                            }
                            return _jsx("span", {});
                        }
                    },
                    {
                        field: 'ActionPlaceholder',
                        renderHeader: () => _jsx("span", {}),
                        width: 50,
                        renderCell: (cell) => {
                            const isReadonly = cell.row.Type === 'ReadOnly';
                            if (isReadonly) {
                                return (_jsx("div", { style: { padding: 3 }, children: _jsx(Tooltip, { title: $t('Data Interchange is Read-only'), children: _jsx(Icon, { iconUrl: AuthenticationIcon }) }) }));
                            }
                            return (_jsx("div", { children: _jsxs(IconMenu, { anchorHorizontalPosition: "left", onOpen: () => {
                                        setSelectedRow({ ...cell.row });
                                        setDataInterchangeNotes(cell.row.Description);
                                    }, children: [_jsx(MenuItem, { onClick: () => setShowDrawer(true), children: "Update Schedule" }), !!cell.row.CompanyConfigFK && (_jsx(MenuItem, { onClick: () => {
                                                window.location.href = `./CorpDataInterchangeConfig.aspx?__ID=${selectedRow.PK}`;
                                            }, children: "Edit Settings" })), !isNull(selectedRow) &&
                                            selectedRow.Inactive !== true &&
                                            selectedRow.RunState !==
                                                DataInterchangeRunState.Running &&
                                            selectedRow.RunState !==
                                                DataInterchangeRunState.Queued &&
                                            selectedRow.Type !== 'ReadOnly' && (_jsx(MenuItem, { onClick: () => {
                                                setShowDrawer(false);
                                                setRunModalOpen(true);
                                            }, children: "Run" }))] }) }));
                        }
                    }
                ], rows: isFilteringData ? filteredData : data }), _jsx(ScrollableDrawer, { open: showDrawer, anchor: "right", sideWidth: 448, openUpdated: setShowDrawer, header: "Update Schedule", footer: selectedRow && (_jsxs(ButtonContainer, { alignButtons: "right", sx: { width: '100%' }, children: [_jsx(Button, { color: "primary", variant: "outlined", disabled: isSavingInterchange, onClick: () => setShowDrawer(false), children: "Cancel" }), _jsx(Button, { color: "primary", disabled: isSavingInterchange, onClick: () => {
                                setIsSavingInterchange(true);
                                saveDataInterchange({
                                    ...selectedRow,
                                    Description: dataInterchangeNotes,
                                    Sched_StartTime: selectedRow.Sched_StartTime
                                }).then((resp) => {
                                    setIsSavingInterchange(false);
                                    if (resp.Status === 'success') {
                                        setShowDrawer(false);
                                        enqueueSnackbar($t('The Data Interchange was updated successfully'), {
                                            variant: 'alert',
                                            AlertProps: { severity: 'success' }
                                        });
                                        // Initiate callback for reloading the loaded Interchange data
                                        updateData();
                                    }
                                    else {
                                        enqueueSnackbar($t('An error was encountered while saving the Data Interchange'), {
                                            variant: 'alert',
                                            AlertProps: { severity: 'error' }
                                        });
                                    }
                                });
                            }, children: "Save" })] })), children: selectedRow && (_jsxs(Stack, { spacing: 2, alignItems: "flex-start", children: [_jsxs(Box, { children: [_jsx(Typography, { variant: "h6", children: selectedRow.Name }), _jsx(FormControlLabel, { control: _jsx(Checkbox, { checked: !selectedRow.Inactive, onChange: () => setSelectedRow({
                                            ...selectedRow,
                                            Inactive: !selectedRow.Inactive
                                        }) }), label: _jsx(Typography, { children: "Active" }) })] }), _jsxs(Stack, { spacing: 1, alignItems: "flex-start", sx: { width: '100%' }, children: [_jsx(Title, { helpText: $t('Data synchronization will be queued to run approximately 15 minutes within the scheduled start time.'), children: $t('Schedule') }), _jsx(FormControlLabel, { control: _jsx(TextField, { type: "time", value: getScheduledStartTime(), onChange: schedStartTimeChangeHandler, style: { marginRight: '8px' } }), label: userTimezoneString, labelPlacement: "end" }), _jsxs("div", { style: { display: 'flex', flexWrap: 'wrap', gap: 8 }, children: [_jsx(DayChip, { label: $t('Monday'), field: "Sched_Mon" }), _jsx(DayChip, { label: $t('Tuesday'), field: "Sched_Tue" }), _jsx(DayChip, { label: $t('Wednesday'), field: "Sched_Wed" }), _jsx(DayChip, { label: $t('Thursday'), field: "Sched_Thu" }), _jsx(DayChip, { label: $t('Friday'), field: "Sched_Fri" }), _jsx(DayChip, { label: $t('Saturday'), field: "Sched_Sat" }), _jsx(DayChip, { label: $t('Sunday'), field: "Sched_Sun" })] })] }), window.AccountConfig.IsAdmin && (_jsxs(Stack, { spacing: 1, alignItems: "flex-start", sx: { width: '100%' }, children: [_jsx(Typography, { variant: "h6", paragraph: true, children: $t('Internal Notes (Certify Employees Only)') }), _jsx(TextField, { value: dataInterchangeNotes, multiline: true, fullWidth: true, rows: 5 })] }))] })) }), _jsx(Modal, { open: runModalOpen, onClose: () => setRunModalOpen(false), modalHeader: "Run Data Interchange", modalFooter: runModalFooter(), children: selectedRow && (_jsxs(Grid, { container: true, direction: "row", style: { marginTop: 5 }, children: [_jsx(Typography, { children: $t("Clicking 'Confirm' will queue the selected interchange to run.") }), _jsx(Typography, { children: $t('Are you sure you want to proceed?') })] })) })] }));
}
export default InterchangeTable;
